import {
  useCreateItemAffiliation,
  useUpdateItemAffiliation,
} from "../api/ItemsApi";
import {
  useCreateImage,
  useGetImagePresignedUrl,
  useUploadImageToS3,
} from "../api/ReportsApi";
import { useFamaFlags } from "../api/FamaFlags";
import {
  AffiliationItemWithMetaData,
  CreateAffiliationApi,
  UpdateAffiliationApi,
} from "../api/Items";
import { CreateImageRequest, ReportSummaryItemApi } from "../api/Reports";

export const useAffiliationManagement = () => {
  const {
    mutate: createAffiliation,
    isLoading: isLoadingAffiliationCreating,
    error: affiliationCreationError,
    isSuccess: successAffiliation,
    data: createdAffiliationUUID,
    reset: resetCreateAffiliation,
  } = useCreateItemAffiliation();

  const {
    mutate: updateAffiliation,
    mutateAsync: updateAffiliationAsync,
    isLoading: isLoadingAffiliationUpdate,
    error: updateAffiliationError,
    isSuccess: successUpdateAffiliation,
    data: updatedAffiliation,
    reset: resetUpdateAffiliation,
  } = useUpdateItemAffiliation();

  const {
    mutateAsync: getImagePresignedUrl,
    isLoading: isLoadingImagePresignedUrl,
  } = useGetImagePresignedUrl();

  const { mutateAsync: createImage, isLoading: isLoadingImageCreating } =
    useCreateImage();

  const { mutateAsync: uploadImageToS3, isLoading: isLoadingImageUploading } =
    useUploadImageToS3();

  const { behaviorMappingActions } = useFamaFlags();

  const isLoading =
    isLoadingAffiliationCreating ||
    isLoadingAffiliationUpdate ||
    isLoadingImageCreating;
  const hasError = !!affiliationCreationError || !!updateAffiliationError;

  const resetAll = () => {
    resetCreateAffiliation();
    resetUpdateAffiliation();
  };

  const createAffiliationWithData = (data: CreateAffiliationApi) => {
    createAffiliation(data);
  };

  const updateAffiliationWithData = (data: UpdateAffiliationApi) => {
    updateAffiliation(data);
  };

  const createImageWithData = async (
    data: CreateImageRequest
  ): Promise<string | undefined> => {
    return await createImage(data);
  };

  return {
    createAffiliation: createAffiliationWithData,
    isLoadingAffiliationCreating,
    affiliationCreationError,
    successAffiliation,
    createdAffiliationUUID,
    resetCreateAffiliation,

    updateAffiliation: updateAffiliationWithData,
    updateAffiliationAsync,
    isLoadingAffiliationUpdate,
    updateAffiliationError,
    successUpdateAffiliation,
    updatedAffiliation,
    resetUpdateAffiliation,

    getImagePresignedUrl,
    createImage: createImageWithData,
    isLoadingImageCreating,
    isLoadingImagePresignedUrl,
    behaviorMappingActions,

    uploadImageToS3,
    isLoadingImageUploading,

    isLoading,
    hasError,
    resetAll,
  };
};

function affiliationItemToAffiliationApi(
  report: ReportSummaryItemApi,
  affiliationContent: AffiliationItemWithMetaData | undefined
): CreateAffiliationApi {
  const clonedContent = Object.assign({}, affiliationContent?.item.content);

  return {
    report_uuid: report?.report_uuid ?? "",
    media_urls: clonedContent.media_urls ?? [],
    url: clonedContent.url ?? "",
    affiliation_source: clonedContent.affiliation_source ?? "",
    affiliation_activity: clonedContent.affiliation_activity ?? "",
    affiliation_type: clonedContent.affiliation_type ?? "",
    text: clonedContent.text ?? "",
    created_at: clonedContent?.created_at?.split("T")?.[0] ?? null,
    ratings: [...(affiliationContent?.rating ?? [])],
  };
}

export { affiliationItemToAffiliationApi };
